export interface UserParameter {
  Name: string;
  Required: boolean;
  Value: string;
}

// Interface to handle API response with typo
export interface UserParameterResponse {
  Name?: string;
  Required?: boolean;
  Requiered?: boolean; // Handle the typo in API response
  Value?: string;
  /**
   * THE SHAPE ACTUALLY ON THE WIRE. GetProjectlist serialises camelCase, items
   * included — observed on beta-dev 2026-09-24:
   *   "userParameter":[{"name":"","requiered":false,"value":"18\" 300' VCP"}]
   * Reading only Name/Value produced {Required:false} for every parameter, so no
   * user-defined grid column could exist and the /info Parameters tab was empty.
   */
  name?: string;
  required?: boolean;
  requiered?: boolean;
  value?: string;
}

export interface ProjectInfoResponse {
  PNAME?: string; // Project Name (old format)
  SER?: string;   // Serial Number (old format)
  LTR?: string;   // Last Telemetry Received (old format)
  
  // New API format
  ProjectName?: string;
  SerialNumber?: string;
  LastTelemetryReceived?: string | null;
  /**
   * Telemetry-DERIVED newest tick, MAX(timestamp) from Telemetry_v2 (epoch ms).
   * Authoritative for liveness — supersedes the stored LTR, which is unreliable
   * (lags in some tenants, leads by months in others).
   */
  lastTelemetryEpochMs?: number | null;
  /**
   * SERVER-DECIDED liveness. Computed by DataServices from the finished flag, a
   * 48 h bound and latest-per-CMS — which correctly handles field disconnects and
   * cold starts that no client-side recency heuristic can see.
   */
  isLive?: boolean | null;
  /** Measurement interval (seconds). Informational — no longer used for liveness. */
  MI?: number | null;
  FiberType?: string;
  Version?: string;
  CustomerName?: string;
  /**
   * Friendly CMS name, joined server-side from the device registry. Optional
   * because most devices are unnamed — consumers fall back to the serial.
   */
  DeviceName?: string;
  /**
   * Cure configuration, as STORED on the ProjectData document: Celsius and metres,
   * never converted for the viewer. Nullable because absent is not zero — a
   * project with no recorded curing temperature was not cured at 0 °C.
   */
  SamplingInterval?: number | null;
  CuringTemperature?: number | null;
  OverheatTemperature?: number | null;
  LinerLength?: number | null;
  CmsModel?: string;
  CmsFirmware?: string;
  /** The cure was closed. Null when the server did not say, which is not "No". */
  Finalized?: boolean | null;
  
  UserParameter: UserParameterResponse[];
}

const finiteOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export class ProjectInfo implements ProjectInfoResponse {
  PNAME: string;
  SER: string;
  LTR: string;
  UserParameter: UserParameter[];
  
  // Properties for both old and new API formats
  ProjectName: string;
  SerialNumber: string;
  LastTelemetryReceived: string | null;
  /**
   * Telemetry-derived newest tick. PREFER THIS over LTR / LastTelemetryReceived
   * everywhere — the stored LTR is unreliable in both directions (observed ~6
   * months ahead of real telemetry in one tenant, a month behind in another).
   *
   * Resolve it via lastTelemetryEpoch() in services/project-liveness rather than
   * reading this field directly, so the derived-then-fallback precedence lives in
   * one place. (No accessor here: project-liveness imports this model, so a
   * back-reference would create an import cycle.)
   */
  lastTelemetryEpochMs: number | null;
  /** SERVER-DECIDED liveness; null when the server has not supplied it. */
  isLive: boolean | null;
  /** Measurement interval in SECONDS (confirmed by DataServices; typically 60). */
  MI: number | null;
  FiberType: string;
  Version: string;
  CustomerName: string;
  /** Friendly CMS name; '' when the device has none. Prefer deviceLabel(). */
  DeviceName: string;
  /** Metres between measurement points, as stored. */
  SamplingInterval: number | null;
  /** Celsius, as stored. */
  CuringTemperature: number | null;
  /** Celsius, as stored. */
  OverheatTemperature: number | null;
  /** Metres of fibre measured, as stored. */
  LinerLength: number | null;
  CmsModel: string;
  CmsFirmware: string;
  Finalized: boolean | null;

  constructor(data: ProjectInfoResponse) {
    // Handle both old and new API formats
    this.PNAME = data.PNAME || data.ProjectName || '';
    this.SER = data.SER || data.SerialNumber || '';
    this.LTR = data.LTR || data.LastTelemetryReceived || '';
    
    // Clean and normalize UserParameter data
    this.UserParameter = (data.UserParameter || []).map(param => ({
      Name: param.Name ?? param.name ?? '',
      Required: param.Required ?? param.Requiered ?? param.required ?? param.requiered ?? false,
      Value: param.Value ?? param.value ?? ''
    }));
    
    // Set properties for both formats
    this.ProjectName = this.PNAME;
    this.SerialNumber = this.SER;
    this.LastTelemetryReceived = this.LTR;
    // Telemetry-derived liveness fields; null until the server supplies them.
    this.lastTelemetryEpochMs =
      typeof data.lastTelemetryEpochMs === 'number' ? data.lastTelemetryEpochMs : null;
    this.MI = typeof data.MI === 'number' ? data.MI : null;
    this.isLive = typeof data.isLive === 'boolean' ? data.isLive : null;
    this.FiberType = data.FiberType || '';
    this.Version = data.Version || '';
    this.CustomerName = data.CustomerName || '';
    this.DeviceName = data.DeviceName || '';
    // EVERY FIELD THIS CONSTRUCTOR DOES NOT NAME IS DROPPED. These seven were
    // offered in the column chooser for months and rendered a dash or "No" on
    // every row, because the server sent them and this list did not copy them.
    this.SamplingInterval = finiteOrNull(data.SamplingInterval);
    this.CuringTemperature = finiteOrNull(data.CuringTemperature);
    this.OverheatTemperature = finiteOrNull(data.OverheatTemperature);
    this.LinerLength = finiteOrNull(data.LinerLength);
    this.CmsModel = data.CmsModel || '';
    this.CmsFirmware = data.CmsFirmware || '';
    this.Finalized = typeof data.Finalized === 'boolean' ? data.Finalized : null;
    
    console.log('ProjectInfo constructor - Input data:', data);
    console.log('ProjectInfo constructor - FiberType:', data.FiberType, '-> Final:', this.FiberType);
    console.log('ProjectInfo constructor - Version:', data.Version, '-> Final:', this.Version);
    console.log('ProjectInfo constructor - CustomerName:', data.CustomerName, '-> Final:', this.CustomerName);
    console.log('ProjectInfo constructor - UserParameter:', this.UserParameter);
  }

  // Helper method to get user parameter keys as a searchable string
  getUserParameterKeys(): string {
    return this.UserParameter.map(param => param.Name).join(', ');
  }

  // Helper method to get user parameter values as a searchable string
  getUserParameterValues(): string {
    return this.UserParameter.map(param => param.Value).join(', ');
  }

  // Helper method to get all user parameters as a searchable string
  getUserParametersText(): string {
    return this.UserParameter.map(param => `${param.Name}: ${param.Value}`).join(', ');
  }

  // Helper method to get user parameter value by name
  getUserParameterValue(paramName: string): string {
    const param = this.UserParameter.find(p => p.Name === paramName);
    return param ? param.Value : '';
  }

  // Static method to get all unique user parameter names from a list of projects
  static getAllUserParameterNames(projects: ProjectInfo[]): string[] {
    const allNames = new Set<string>();
    projects.forEach(project => {
      project.UserParameter.forEach(param => {
        allNames.add(param.Name);
      });
    });
    return Array.from(allNames).sort();
  }
}
